// GamesStatsPage.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import EditMatchModal from "./EditMatchModal.js";
import AddMatchModal from "./AddMatchModal.js";
import "./css/GamesStatsPage.css";
import useSubscriptionStatus from "./hooks/useSubscriptionStatus";
import config from "./config";

const devLog = (...args) => {
  if (config.IS_DEV) {
    console.log(...args);
  }
};

const API_URL_SUB =
  "https://lykns00a87.execute-api.us-east-2.amazonaws.com/default/football_app_data_crud";

const API_URL =
  "https://vhlyllb4ea.execute-api.us-east-2.amazonaws.com/prod/matchStatsAPI";

const GamesStatsPage = ({ userId }) => {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [freeTierUsed, setFreeTierUsed] = useState(false);
  const [gamesLeft, setGamesLeft] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // States for Modals
  const [editMatchModalOpen, setEditMatchModalOpen] = useState(false);
  const [editMatchData, setEditMatchData] = useState({
    userId: "",
    teamId: "",
    matchId: "",
  });

  const [addMatchModalOpen, setAddMatchModalOpen] = useState(false);

  // Delete Modal State
  const [deleteMatchModalOpen, setDeleteMatchModalOpen] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const {
    subscriptionStatus: subscriptionType,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useSubscriptionStatus(userId);

  useEffect(() => {
    devLog("useEffect triggered: userId changed:", userId);
    if (userId) {
      devLog("Fetching all matches and teams for coach:", userId);
      fetchAllMatchesForCoach();
    } else {
      setError("No userId provided; cannot fetch matches.");
      devLog("No userId provided, skipping fetchAllMatchesForCoach.");
    }
  }, [userId]);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await axios.post(API_URL_SUB, {
          operationType: "check_coach_subscription",
          data: { coach_id: userId },
        });

        const data =
          typeof response.data.body === "string"
            ? JSON.parse(response.data.body)
            : response.data.body;

        // Directly access the correct values
        setCurrentPlan(data.subscription_type || "unknown"); // Adjusted to match backend response
        setFreeTierUsed(data.free_tier_used || false); // Boolean already converted
        setGamesLeft(data.games_left_this_month || 0); // No need for parseInt as backend sends number
        setSubscriptionStatus(data.subscription_status || "unknown");
      } catch (err) {
        console.error("Error fetching subscription details:", err);
        setError("Failed to fetch subscription details.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [userId]);
  /**
   * 1) Fetch all matches and teams for the coach => "fetch_all_matches_for_coach"
   */
  const fetchAllMatchesForCoach = async () => {
    setLoading(true);
    setError("");
    devLog("fetchAllMatchesForCoach() called with userId:", userId);

    const requestBody = {
      operationType: "fetch_all_matches_for_coach",
      data: {
        coach_id: userId,
      },
    };
    devLog("Posting request to API with body:", requestBody);

    try {
      const response = await axios.post(API_URL, requestBody, {
        headers: { "Content-Type": "application/json" },
      });
      devLog("Response from fetchAllMatchesForCoach:", response);

      if (response.status === 200) {
        let { matches, teams } = response.data;
        devLog("Matches received:", matches);
        devLog("Teams received:", teams);

        // Ensure matches and teams are arrays
        matches = matches || [];
        teams = teams || [];

        // Sort matches by date descending (latest first)
        matches.sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
        devLog("Matches after sorting:", matches);

        setMatches(matches);
        setTeams(teams);
        devLog("Teams state set to:", teams);
      } else {
        const errMsg =
          response.data.error || "Failed to fetch matches for the coach.";
        setError(errMsg);
        devLog("Non-200 status, setting error:", errMsg);
      }
    } catch (err) {
      devLog("Error during fetchAllMatchesForCoach:", err);
      setError("Unable to fetch matches. Check console.");
    } finally {
      setLoading(false);
      devLog("fetchAllMatchesForCoach finished loading.");
    }
  };

  /**
   * 2) Open Edit Match Modal
   */
  const openEditMatchModal = (match) => {
    // Derive the actual IDs from PK and SK
    const pk = match.PK || "";
    const sk = match.SK || "";
    const extractedTeamId = pk.startsWith("TEAM#") ? pk.split("#")[1] : "";
    const extractedMatchId = sk.startsWith("MATCH#") ? sk.split("#")[1] : "";

    setEditMatchData({
      userId,
      teamId: extractedTeamId,
      matchId: extractedMatchId,
    });
    setEditMatchModalOpen(true);
    devLog(
      `Opening EditMatchModal for team_id: ${extractedTeamId}, match_id: ${extractedMatchId}`
    );
  };

  /**
   * 3) Open Add Match Modal
   */
  const openAddMatchModal = () => {
    if (teams.length === 0) {
      setError("No teams available to add a match.");
      devLog("No teams available to add a match.");
      return;
    }
    setAddMatchModalOpen(true);
    devLog("Opening AddMatchModal.");
  };

  /**
   * 4) Close Add Match Modal
   */
  const closeAddMatchModal = () => {
    setAddMatchModalOpen(false);
    devLog("Closing AddMatchModal.");
  };

  /**
   * Helper function to format the date
   */
  const formatDate = (dateString) => {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      devLog("Error formatting date:", error);
      return dateString; // Fallback to original string if formatting fails
    }
  };

  /**
   * Generate a unique key for each match.
   * Since `match_id` is removed, use a combination of other unique fields.
   * If `PK` and `SK` are unique per match, they can be used.
   */
  const generateMatchKey = (match) => {
    const pk = match.PK || "UNKNOWN_PK";
    const sk = match.SK || "UNKNOWN_SK";
    return `${pk}-${sk}`;
  };

  /**
   * Open Delete Confirmation Modal
   */
  const openDeleteMatchModal = (match) => {
    setMatchToDelete(match);
    setDeleteMatchModalOpen(true);
    devLog("Opening DeleteMatchModal for match:", match);
  };

  /**
   * Close Delete Confirmation Modal
   */
  const closeDeleteMatchModal = () => {
    setDeleteMatchModalOpen(false);
    setMatchToDelete(null);
    setDeleteError("");
    devLog("Closing DeleteMatchModal.");
  };

  /**
   * Delete Match Function
   */
  const deleteMatch = async () => {
    if (!matchToDelete) {
      devLog("No match selected for deletion.");
      return;
    }

    const { PK, SK } = matchToDelete;
    const teamId = PK.split("#")[1];
    const matchId = SK.split("#")[1];

    const requestBody = {
      operationType: "delete_match",
      data: { team_id: teamId, match_id: matchId },
    };

    devLog("Sending delete request with body:", requestBody);
    setDeleting(true);
    setDeleteError("");

    try {
      const response = await axios.post(API_URL, requestBody, {
        headers: { "Content-Type": "application/json" },
      });

      devLog("Response from deleteMatch API:", response);

      if (response.status === 200) {
        // Remove the deleted match from the state
        setMatches((prevMatches) =>
          prevMatches.filter((m) => m.SK !== SK || m.PK !== PK)
        );
        devLog("Match deleted successfully.");
      } else {
        const errMsg =
          response.data.error ||
          "Failed to delete the match. Please try again.";
        setDeleteError(errMsg);
        devLog("Failed to delete match:", errMsg);
      }
    } catch (err) {
      devLog("Error during deleteMatch:", err);
      setDeleteError(
        "Unable to delete the match. Please check your connection."
      );
    } finally {
      setDeleting(false);
      closeDeleteMatchModal();
    }
  };

  return (
    <div className="gs-games-stats-container">
      <h1 className="gs-header-title">Manage Games Stats</h1>
      {error && <div className="gs-error-message">{error}</div>}
      {loading && (
        <div className="loading-container">
          <div className="football-spinner"></div>
          <span>Loading...</span>
        </div>
      )}

      {/* Add Match Button */}
      <div className="gs-add-match-button-container">
        <div>
          {subscriptionLoading ? (
            <div className="loading-container">
              <div className="football-spinner"></div>
              <p>Checking subscription status...</p>
            </div>
          ) : (
            <>
              <button
                className={`gs-edit-btn ${
                  currentPlan !== "Stats Plan" &&
                  currentPlan !== "AI Match Reports Plan"
                    ? "disabled"
                    : ""
                }`}
                onClick={
                  currentPlan === "Stats Plan" ||
                  currentPlan === "AI Match Reports Plan"
                    ? openAddMatchModal
                    : null
                }
                aria-label="Add Match"
                disabled={
                  (currentPlan !== "Stats Plan" &&
                    currentPlan !== "AI Match Reports Plan") ||
                  teams.length === 0 ||
                  loading
                }
                title={
                  currentPlan !== "Stats Plan" &&
                  currentPlan !== "AI Match Reports Plan"
                    ? "Only available on Stats Plan or AI Match Reports Plan"
                    : teams.length === 0
                    ? "No teams available to add a match."
                    : "Add a new match"
                }
              >
                Add Match
              </button>
              {(currentPlan !== "Stats Plan" || teams.length === 0) && (
                <p style={{ marginTop: "5px", fontSize: "0.9rem" }}>
                  Only available on Stats Plan{" "}
                  <a
                    href="/manage-subscription"
                    style={{
                      fontWeight: "bold",
                      color: "blue",
                      textDecoration: "underline",
                    }}
                  >
                    Review Your Plan
                  </a>
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Existing Match Table */}
      <table className="gs-game-table" style={{ marginTop: 20 }}>
        <thead>
          <tr>
            {/* Removed Team ID column */}
            <th>My Team</th>
            <th>Opposition</th>
            <th>Date</th>
            <th>Score</th>
            <th>Home/Away</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((m) => {
            const myTeamScore = m.teamScores?.myTeamScore ?? 0;
            const oppScore = m.teamScores?.oppositionScore ?? 0;
            const away = m.homeAway
              ? m.homeAway.trim().toLowerCase() === "away"
              : false;

            // Determine score display based on home/away
            const displayScore = away
              ? `${oppScore} - ${myTeamScore}`
              : `${myTeamScore} - ${oppScore}`;

            return (
              <tr key={generateMatchKey(m)}>
                {/* Removed Team ID cell */}
                <td>{m.myTeam}</td>
                <td>{m.opposition}</td>
                <td>{formatDate(m.match_date)}</td>
                <td>{displayScore}</td>
                <td>{m.homeAway}</td>
                <td className="gs-actions-cell">
                  <button
                    className="gs-edit-btn"
                    onClick={() => openEditMatchModal(m)}
                    aria-label={`Edit match against ${
                      m.opposition
                    } on ${formatDate(m.match_date)}`}
                  >
                    Edit
                  </button>
                  <button
                    className="gs-delete-btn"
                    onClick={() => openDeleteMatchModal(m)}
                    aria-label={`Delete match against ${
                      m.opposition
                    } on ${formatDate(m.match_date)}`}
                    style={{ marginLeft: "10px" }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Delete Confirmation Modal */}
      {deleteMatchModalOpen && (
        <div className="gs-edit-match-modal-overlay">
          <div className="gs-edit-match-modal-content">
            <span
              className="gs-edit-match-modal-close-button"
              onClick={closeDeleteMatchModal}
              aria-label="Close Delete Confirmation Modal"
            >
              &times;
            </span>
            <h2>Confirm Deletion</h2>
            {deleteError && (
              <div className="gs-edit-match-modal-error-message">
                {deleteError}
              </div>
            )}
            <p>
              Are you sure you want to delete the match against{" "}
              <strong>{matchToDelete.opposition}</strong> on{" "}
              <strong>{formatDate(matchToDelete.match_date)}</strong>?
            </p>
            <div className="gs-edit-match-modal-buttons">
              <button
                className="gs-save-button"
                onClick={deleteMatch}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                className="gs-cancel-button"
                onClick={closeDeleteMatchModal}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MATCH MODAL */}
      {editMatchModalOpen && (
        <EditMatchModal
          isOpen={editMatchModalOpen}
          onClose={() => {
            devLog("Closing edit match modal");
            setEditMatchModalOpen(false);
          }}
          userId={editMatchData.userId}
          teamId={editMatchData.teamId}
          matchId={editMatchData.matchId}
          refreshMatches={() => {
            devLog("refreshMatches called from EditMatchModal");
            fetchAllMatchesForCoach();
          }}
        />
      )}

      {/* ADD MATCH MODAL */}
      {addMatchModalOpen && (
        <AddMatchModal
          isOpen={addMatchModalOpen}
          onClose={closeAddMatchModal}
          userId={userId}
          teams={teams} // Pass the list of teams
          refreshMatches={fetchAllMatchesForCoach}
        />
      )}
    </div>
  );
};

export default GamesStatsPage;
